import { useState, useContext } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { Badge, Button, Collapsible, CollapsibleContent, CollapsibleTrigger, Skeleton, type MapWorkbenchRuntimeSurfaceSectionRenderArgs } from '@workspace/ui';

import { readRuntimeSurfaceSectionComponentId } from '@/components/mapping/runtimeSurfaceRenderers';
import type { SalesLocationRelationshipSummary } from '@/lib/sales-location-summaries';
import { SalesMapRoutingContext } from './SalesMapRoutingQuoteSection';
import { formatDateTime, useSalesLocationSummary } from './useSalesLocationSummary';
import { BadgeRow } from './salesMapShared';

export const SALES_MAP_DETAILS_COMPONENT_ID = 'sales_location_details';

const readString = (value: unknown) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

const displayValue = (value: unknown) => readString(value) ?? '—';

const buildCooldownOwnerLabel = (relationshipSummary: SalesLocationRelationshipSummary) =>
  relationshipSummary.cooldown_owner_username ||
  relationshipSummary.cooldown_owner_email ||
  relationshipSummary.cooldown_owner_user_id ||
  'another seller context';

export const isSalesMapDetailsSection = (section: MapWorkbenchRuntimeSurfaceSectionRenderArgs['section']) =>
  section.kind === 'custom' && readRuntimeSurfaceSectionComponentId(section) === SALES_MAP_DETAILS_COMPONENT_ID;

function SalesMapDetailsSectionBody({ feature }: MapWorkbenchRuntimeSurfaceSectionRenderArgs) {
  const routingContext = useContext(SalesMapRoutingContext);
  const { requestLocation, summary, error, isLoading, refresh } = useSalesLocationSummary(
    feature,
    routingContext?.salesContext,
  );
  const [metaOpen, setMetaOpen] = useState(false);
  const properties = feature.properties ?? {};
  const relationshipSummary = summary?.location_relationship_summary ?? null;

  if (!requestLocation) {
    return <p className="text-sm text-muted-foreground">Location identity is unavailable for details.</p>;
  }

  const statusBadges = relationshipSummary ? [
    { key: 'state', node: <Badge variant="muted">{relationshipSummary.relationship_state}</Badge> },
    ...(relationshipSummary.has_active_service ? [{ key: 'active', node: <Badge variant="success">Active service</Badge> }] : []),
    ...(relationshipSummary.do_not_contact ? [{ key: 'dnc', node: <Badge variant="destructive">Do not contact</Badge> }] : []),
    ...(relationshipSummary.follow_up_required ? [{ key: 'followup', node: <Badge variant="outline">Follow up required</Badge> }] : []),
    ...(relationshipSummary.follow_up_due_at ? [{ key: 'due', node: <Badge variant="outline">Due {formatDateTime(relationshipSummary.follow_up_due_at)}</Badge> }] : []),
    ...(relationshipSummary.interest_status === 'interested' ? [{ key: 'interested', node: <Badge variant="success">Interested</Badge> }] : []),
    ...(relationshipSummary.interest_status === 'not_interested' ? [{ key: 'not_interested', node: <Badge variant="muted">Not interested</Badge> }] : []),
    ...(relationshipSummary.callback_status === 'requested' ? [{ key: 'cb_req', node: <Badge variant="outline">Callback requested</Badge> }] : []),
    ...(relationshipSummary.callback_status === 'completed' ? [{ key: 'cb_done', node: <Badge variant="muted">Callback completed</Badge> }] : []),
    ...(relationshipSummary.note_count > 0 ? [{
      key: 'notes',
      node: <Badge variant="outline">{relationshipSummary.note_count} {relationshipSummary.note_count === 1 ? 'note' : 'notes'}</Badge>,
    }] : []),
  ] : [];

  return (
    <section className="space-y-4">
      {isLoading ? (
        <div className="space-y-3">
          <div className="flex gap-2">
            <Skeleton className="h-6 w-24 rounded-full" />
            <Skeleton className="h-6 w-24 rounded-full" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-[4.5rem] rounded-lg" />
            <Skeleton className="h-[4.5rem] rounded-lg" />
            <Skeleton className="h-[4.5rem] rounded-lg" />
            <Skeleton className="h-[4.5rem] rounded-lg" />
          </div>
        </div>
      ) : null}

      {!isLoading && statusBadges.length > 0 ? (
        <BadgeRow badges={statusBadges} />
      ) : null}

      {error ? (
        <div className="rounded-md bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <div>{error instanceof Error ? error.message : 'Failed to load location details.'}</div>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 h-auto px-0 py-0 text-xs text-destructive hover:bg-transparent hover:text-destructive/80"
            onClick={() => void refresh()}
          >
            Try again
          </Button>
        </div>
      ) : null}

      {!isLoading && relationshipSummary?.cooldown_active ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900">
          <div className="font-semibold">
            {relationshipSummary.cooldown_blocks_current_context ? 'Cooldown blocks this seller context.' : 'Cooldown is active for this location.'}
          </div>
          <div className="mt-1 text-xs text-amber-900/80">
            Owned by {buildCooldownOwnerLabel(relationshipSummary)}
            {relationshipSummary.cooldown_expires_at ? ` until ${formatDateTime(relationshipSummary.cooldown_expires_at)}.` : '.'}
          </div>
        </div>
      ) : null}

      {!isLoading ? (
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-border/70 bg-background px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Address</div>
            <div
              className="mt-1 truncate text-sm font-medium text-foreground"
              title={requestLocation.address_line1 ?? requestLocation.display_name ?? undefined}
            >
              {displayValue(requestLocation.address_line1 ?? requestLocation.display_name)}
            </div>
            <div className="mt-1 truncate text-xs text-muted-foreground">
              {[requestLocation.locality, requestLocation.region_code, requestLocation.postal_code].filter(Boolean).join(', ') || '—'}
            </div>
          </div>
          <div className="rounded-lg border border-border/70 bg-background px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Business</div>
            <div
              className="mt-1 truncate text-sm font-medium text-foreground"
              title={readString(properties.business_name) ?? undefined}
            >
              {displayValue(properties.business_name)}
            </div>
            <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70">Occupancy</div>
            <div className="truncate text-xs text-muted-foreground">{displayValue(properties.occupancy_kind)}</div>
          </div>
          <div className="rounded-lg border border-border/70 bg-background px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Serviceability</div>
            <div className="mt-1 text-sm font-medium text-foreground">{displayValue(properties.serviceability_status)}</div>
            <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70">Distance quality</div>
            <div className="text-xs text-muted-foreground">{displayValue(properties.distance_quality)}</div>
          </div>
          <div className="rounded-lg border border-border/70 bg-background px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Relationship</div>
            <div className="mt-1 text-sm font-medium text-foreground">
              {relationshipSummary ? `${relationshipSummary.active_service_count} active service${relationshipSummary.active_service_count === 1 ? '' : 's'}` : '—'}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {relationshipSummary ? `${relationshipSummary.relationship_count} related record${relationshipSummary.relationship_count === 1 ? '' : 's'}` : '—'}
            </div>
          </div>
        </div>
      ) : null}

      <Collapsible open={metaOpen} onOpenChange={setMetaOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:text-foreground"
          >
            {metaOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Source metadata
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 rounded-lg border border-border/70 bg-muted/20 px-4 py-3">
            <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
              <div>
                <span className="text-muted-foreground">Location ID:</span>{' '}
                <span className="font-medium text-foreground">{requestLocation.location_id ?? '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Feature ID:</span>{' '}
                <span className="font-medium text-foreground">{displayValue(feature.id)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Source:</span>{' '}
                <span className="font-medium text-foreground">{displayValue(feature.source)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Layer:</span>{' '}
                <span className="font-medium text-foreground">{displayValue(feature.sourceLayer)}</span>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

export function renderSalesMapDetailsSection(args: MapWorkbenchRuntimeSurfaceSectionRenderArgs) {
  if (!isSalesMapDetailsSection(args.section)) {
    return null;
  }
  return <SalesMapDetailsSectionBody {...args} />;
}
